import { Head, Link, usePage } from "@inertiajs/react"
import { Mail, MapPin, Pencil } from "lucide-react"

import {
  WaitingOnBadge,
  WorkOrderTypeChip,
} from "@/components/products/fleet/badges"
import { EMPTY, formatDays, humanize } from "@/components/products/fleet/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem, Facility, SharedProps } from "@/types"

interface MruTeamRow {
  id: number
  name: string
  vendor_company: string | null
  active: boolean
  contacts: Record<string, unknown>[]
}

interface OpenWorkOrderRow {
  id: number
  type: string
  status: string
  car_id: number | null
  car: string | null
  defect_category: string | null
  waiting_on: string | null
  days_waiting: number | null
  shop: string | null
  mru_team: string | null
}

interface SlaStatus {
  on_site: number
  limit: number | null
  level: "good" | "warning" | "critical" | "unmeasured"
}

interface Props {
  facility: Facility
  mru_teams: MruTeamRow[]
  open_work_orders: OpenWorkOrderRow[]
  sla_status: SlaStatus
  can_edit_facility: boolean
}

export default function FacilityShow({
  facility,
  mru_teams,
  open_work_orders,
  sla_status,
  can_edit_facility,
}: Props) {
  const routes = useOrgRoutes()
  const orgSubdomain =
    usePage<SharedProps>().props.organization?.email_subdomain || ""

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: facility.name, href: routes.facilityPath(facility.id) },
  ]

  const inboundAddress = facility.email_local_part
    ? `${facility.email_local_part}@${orgSubdomain || "<subdomain>"}.rebulk.com`
    : null

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={facility.name} />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">
                {facility.name}
              </h1>
              {facility.active === false && (
                <Badge variant="secondary">Inactive</Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              {(facility.city || facility.state) && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="size-3.5" aria-hidden="true" />
                  {[facility.city, facility.state].filter(Boolean).join(", ")}
                </span>
              )}
              {facility.timezone && <span>{facility.timezone}</span>}
            </p>
          </div>
          {can_edit_facility && (
            <Button variant="outline" size="sm" asChild>
              <Link href={routes.editFacilityPath(facility.id)}>
                <Pencil className="mr-1 size-4" /> Edit
              </Link>
            </Button>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>Bad-order SLA</CardTitle>
            </CardHeader>
            <CardContent>
              {facility.sla_bad_order_limit ? (
                <>
                  <p className="text-2xl font-semibold tabular-nums">
                    {sla_status.on_site}
                    <span className="text-muted-foreground ml-1 text-sm font-normal">
                      of {sla_status.limit} allowed on site
                    </span>
                  </p>
                  <p
                    className={cn(
                      "mt-1 text-sm",
                      sla_status.level === "critical"
                        ? "font-medium text-red-600 dark:text-red-400"
                        : sla_status.level === "warning"
                          ? "font-medium text-amber-600 dark:text-amber-400"
                          : "text-muted-foreground",
                    )}
                  >
                    {sla_status.level === "critical"
                      ? "Over the on-site limit — operations impacted"
                      : sla_status.level === "warning"
                        ? "Approaching the on-site limit"
                        : "Within the on-site limit"}
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No SLA set. This facility is left out of the compliance board
                  until one is.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Inbound mail</CardTitle>
            </CardHeader>
            <CardContent>
              {inboundAddress ? (
                <>
                  <p className="inline-flex items-center gap-2 text-sm break-all">
                    <Mail
                      className="text-muted-foreground size-4 shrink-0"
                      aria-hidden="true"
                    />
                    <span className="font-medium">{inboundAddress}</span>
                  </p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Documents mailed here are read, extracted and filed against
                    this facility&rsquo;s cars.
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No inbound address yet. Set a local-part on the edit page to
                  start accepting documents by email.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Open work</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold tabular-nums">
                {open_work_orders.length}
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                work orders still open here
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>MRU teams</CardTitle>
          </CardHeader>
          <CardContent>
            {mru_teams.length === 0 ? (
              <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
                No mobile repair crews are based here. Field repair is the fast
                path — the car is fixed where it stands instead of being routed
                to a shop.
              </p>
            ) : (
              <ul className="divide-border divide-y">
                {mru_teams.map((team) => (
                  <li
                    key={team.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2"
                  >
                    <div>
                      <span className="font-medium">{team.name}</span>
                      {team.vendor_company && (
                        <span className="text-muted-foreground text-sm">
                          {" "}
                          · {team.vendor_company}
                        </span>
                      )}
                    </div>
                    {!team.active && (
                      <Badge variant="secondary" className="text-xs">
                        Inactive
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Open work orders</CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">Car</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Waiting on</TableHead>
                  <TableHead className="text-right">Days waiting</TableHead>
                  <TableHead className="pr-6">Where</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {open_work_orders.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-muted-foreground py-8 pl-6 text-sm"
                    >
                      Nothing is open at this facility.
                    </TableCell>
                  </TableRow>
                ) : (
                  open_work_orders.map((wo) => (
                    <TableRow key={wo.id}>
                      <TableCell className="pl-6 font-medium">
                        {wo.car_id ? (
                          <Link
                            href={routes.carPath(wo.car_id)}
                            className="hover:underline"
                          >
                            {wo.car ?? EMPTY}
                          </Link>
                        ) : (
                          (wo.car ?? EMPTY)
                        )}
                      </TableCell>
                      <TableCell>
                        <Link href={routes.workOrderPath(wo.id)}>
                          <WorkOrderTypeChip type={wo.type} />
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">
                        {humanize(wo.status)}
                      </TableCell>
                      <TableCell>
                        <WaitingOnBadge waitingOn={wo.waiting_on} />
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatDays(wo.days_waiting)}
                      </TableCell>
                      <TableCell className="pr-6 text-sm">
                        {wo.shop ?? wo.mru_team ?? EMPTY}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
